'use client';

import React, { useState } from 'react';
import countries from 'flag-icons/country.json';

const COUNTRY_CODES = new Set(countries.map(({ code }) => code));

/**
 * Mapping language IDs to ISO country flag codes used by flag-icons library.
 */
const LANGUAGE_FLAG_MAP: Record<string, string> = {
    km: 'kh', // Khmer -> Cambodia flag
    kh: 'kh',
    vn: 'vn', // Vietnamese
    vi: 'vn',
    en: 'gb', // Preserve the project's English flag convention.
    vie: 'vn', eng: 'gb', khm: 'kh', jpn: 'jp', kor: 'kr',
    zho: 'cn', chi: 'cn', lao: 'la', tha: 'th', fra: 'fr', fre: 'fr',
    deu: 'de', ger: 'de', rus: 'ru', spa: 'es', ita: 'it',
    ja: 'jp', // Japanese -> Japan flag
    jp: 'jp',
    ko: 'kr', // Korean -> South Korea flag
    kr: 'kr',
    zh: 'cn', // Chinese -> China flag
    cn: 'cn',
    lo: 'la', // Lao -> Laos flag
    la: 'la',
    th: 'th', // Thai -> Thailand flag
    fr: 'fr', // French -> France flag
    de: 'de', // German -> Germany flag
    ru: 'ru', // Russian -> Russia flag
    es: 'es', // Spanish -> Spain flag
    it: 'it', // Italian -> Italy flag
};

export const getCountryCodeForLanguage = (langId?: string): string => {
    if (!langId) return '';
    const clean = String(langId).trim().toLowerCase().replace(/_/g, '-');
    if (LANGUAGE_FLAG_MAP[clean]) return LANGUAGE_FLAG_MAP[clean];
    try {
        const locale = new Intl.Locale(clean);
        const region = (locale.region || locale.maximize().region || '').toLowerCase();
        if (COUNTRY_CODES.has(region)) return region;
    } catch { /* Unknown/custom language IDs use the text fallback. */ }
    return COUNTRY_CODES.has(clean) ? clean : '';
};

export const resolveLanguageFlagCode = (avatar?: string, languageId?: string): string => {
    const code = String(avatar || '').trim().toLowerCase();
    // Legacy rows sometimes store the language ID (km, en, ja) as the flag.
    if (code && code !== String(languageId || '').trim().toLowerCase() && COUNTRY_CODES.has(code)) return code;
    return getCountryCodeForLanguage(languageId || code);
};

interface LanguageFlagProps {
    avatar?: string;
    languageId?: string;
    fullName?: string;
    className?: string;
    imgClassName?: string;
    square?: boolean;
}

export default function LanguageFlag({
    avatar,
    languageId,
    fullName = '',
    className = 'w-full h-full',
    imgClassName = 'w-full h-full object-cover',
    square = true,
}: LanguageFlagProps) {
    const [failedAvatar, setFailedAvatar] = useState<string | null>(null);

    const countryCode = resolveLanguageFlagCode(avatar, languageId);
    const imageSource = String(avatar || '').trim();
    const hasValidAvatar = /^(https?:\/\/|\/|data:image\/|blob:)/i.test(imageSource) && failedAvatar !== imageSource;

    if (hasValidAvatar) {
        return (
            <img
                src={imageSource}
                alt={fullName || languageId || 'flag'}
                className={imgClassName}
                onError={() => setFailedAvatar(imageSource)}
            />
        );
    }

    if (countryCode) {
        return (
            <span
                className={`fi fi-${countryCode} ${square ? 'fis' : ''} ${className} block bg-cover bg-center`}
                style={{ display: 'block', width: '100%', height: '100%', backgroundSize: 'cover', backgroundPosition: 'center' }}
                title={fullName || languageId}
            />
        );
    }

    return (
        <div className={`${className} flex items-center justify-center bg-gray-500 text-white font-bold text-xs uppercase select-none`}>
            {languageId ? languageId.slice(0, 2) : '??'}
        </div>
    );
}
